<template>
  <!-- Toast/Banner - First interaction -->
  <Transition name="slide-up">
    <div
      v-if="showToast"
      class="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 md:px-6 md:pb-6"
    >
      <div
        class="mx-auto max-w-md bg-linear-to-r from-teal-800 to-teal-900 rounded-2xl overflow-hidden"
      >
        <!-- Content Container with glassmorphism effect -->
        <div class="relative p-4">
          <!-- Header with icon and text -->
          <div class="flex items-start gap-4 mb-4">
            <!-- Icon -->
            <div class="shrink-0">
              <div
                class="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center"
              >
                <img
                  src="@/app/assets/img/munzen/white-symbol.svg"
                  alt="Munzen"
                  class="h-7 w-7"
                />
              </div>
            </div>

            <!-- Text Content -->
            <div class="flex-1 pt-1">
              <h3 class="text-white font-bold text-base md:text-lg mb-1">
                Instala Munzen en tu iPhone/iPad
              </h3>
              <p class="text-teal-50 text-sm leading-relaxed">
                Accede más rápido a tu facturación con nuestra app
              </p>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-col gap-2">
            <!-- Primary Action -->
            <v-btn
              @click="handleShowInstructions"
              color="white"
              variant="flat"
              size="large"
              block
              class="text-teal-700! font-semibold normal-case! shadow-lg! hover:shadow-xl! transition-shadow"
              prepend-icon="fas fa-arrow-down-to-bracket"
            >
              Ver cómo instalar
            </v-btn>

            <!-- Secondary Actions Row -->
            <div class="flex gap-2">
              <v-btn
                @click="dismissPermanently"
                variant="text"
                size="small"
                class="text-white/90! hover:text-white! normal-case! flex-1 text-xs!"
              >
                No volver a mostrar
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Instructions Modal - Second interaction -->
  <BaseModal
    v-model:visible="isInstructionsModalOpen"
    color="teal"
    size="md"
    :show-action-buttons="false"
  >
    <!-- Icon -->
    <template #title-icon>
      <i class="fas fa-mobile-screen text-white"></i>
    </template>

    <!-- Title -->
    <template #title> Cómo instalar Munzen </template>

    <!-- Subtitle -->
    <template #sub-title> Sigue estos pasos para agregar Munzen a tu pantalla de inicio </template>

    <!-- Instructions Content -->
    <div class="space-y-6">
      <!-- Step 1 -->
      <!-- Step 1 -->
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-3">
          <div class="flex items-center justify-center h-8 w-8 rounded-full bg-teal-100">
            <span class="text-teal-700 font-bold text-sm">1</span>
          </div>
          <h3 class="font-semibold text-gray-900 text-base">Toca el botón de compartir</h3>
        </div>
        <div class="pl-2">
          <p class="text-gray-600 text-sm mb-3 leading-relaxed">
            Busca el botón de compartir en la barra de herramientas de Safari
          </p>
          <div class="bg-gray-100 rounded-xl p-4 flex items-center justify-center">
            <v-icon
              icon="fas fa-share-nodes"
              size="40"
              class="text-teal-600"
            />
          </div>
        </div>
      </div>

      <!-- Divider -->
      <div class="w-full h-px bg-gray-200"></div>

      <!-- Step 2 -->
      <!-- Step 2 -->
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-3">
          <div class="flex items-center justify-center h-8 w-8 rounded-full bg-teal-100">
            <span class="text-teal-700 font-bold text-sm">2</span>
          </div>
          <h3 class="font-semibold text-gray-900 text-base">
            Selecciona "Agregar a pantalla de inicio"
          </h3>
        </div>
        <div class="pl-2">
          <p class="text-gray-600 text-sm leading-relaxed">
            Desplázate en el menú de compartir y busca esta opción (puede estar al final)
          </p>
        </div>
      </div>

      <!-- Divider -->
      <div class="w-full h-px bg-gray-200"></div>

      <!-- Step 3 -->
      <!-- Step 3 -->
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-3">
          <div class="flex items-center justify-center h-8 w-8 rounded-full bg-teal-100">
            <span class="text-teal-700 font-bold text-sm">3</span>
          </div>
          <h3 class="font-semibold text-gray-900 text-base">Confirma tocando "Agregar"</h3>
        </div>
        <div class="pl-2">
          <p class="text-gray-600 text-sm leading-relaxed">
            Munzen aparecerá en tu pantalla de inicio como una app nativa
          </p>
        </div>
      </div>

      <!-- Info Box -->
      <div
        class="bg-linear-to-r from-blue-50 to-teal-50 border border-teal-200 rounded-xl p-4 mt-6"
      >
        <div class="flex gap-3">
          <v-icon
            icon="fas fa-lightbulb"
            class="text-teal-600 shrink-0 mt-0.5"
            size="20"
          />
          <p class="text-gray-700 text-sm leading-relaxed">
            Una vez instalada, podrás acceder a Munzen como una aplicación nativa.
          </p>
        </div>
      </div>

      <!-- Action Button -->
      <div class="pt-4">
        <v-btn
          @click="handleCloseModal"
          color="teal"
          variant="flat"
          size="large"
          block
          prepend-icon="fas fa-check"
          class="font-semibold!"
        >
          Entendido
        </v-btn>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
  import { usePwaInstall } from '@/app/pwa/composables/usePwaInstall';
  import BaseModal from '@/common/views/partials/BaseModal.vue';

  /**
   * @author Omar Sánchez Martínez
   * @since 2025-11-19
   * @updated 2025-11-21
   * @description iOS PWA Installation Prompt Component
   *
   * Two-step installation flow for better UX:
   * 1. Toast/Banner: Small, non-intrusive prompt at the bottom of screen
   *    - Shows automatically on first visit (iOS only)
   *    - Options to dismiss temporarily or permanently
   *    - Call-to-action to view instructions
   *
   * 2. Modal: Detailed step-by-step instructions
   *    - Only shown when user clicks "Ver cómo instalar"
   *    - Uses BaseModal component for consistency
   *    - Contains visual guide for iOS installation
   *
   * Features:
   * - Detects iOS devices automatically
   * - Shows only if app is not already installed (standalone mode)
   * - Respects user preferences (permanent dismissal)
   * - 30-day temporary dismissal option
   * - Premium design with gradients and glassmorphism
   * - Smooth animations and transitions
   * - Fully responsive for all iPhone sizes
   *
   * @example
   * <PwaInstallPrompt />
   */

  const { showToast, isInstructionsModalOpen, dismissToast, dismissPermanently, showInstructions } =
    usePwaInstall();

  /**
   * Handle showing instructions
   * Dismisses the toast and opens the modal
   */
  const handleShowInstructions = (): void => {
    dismissToast();
    showInstructions();
  };

  /**
   * Handle closing the modal
   * Simply closes the modal, doesn't affect toast dismissal state
   */
  const handleCloseModal = (): void => {
    isInstructionsModalOpen.value = false;
  };
</script>

<style scoped>
  /* Slide-up animation for toast */
  .slide-up-enter-active {
    animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .slide-up-leave-active {
    animation: slideDown 0.3s cubic-bezier(0.4, 0, 1, 1);
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideDown {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(100%);
    }
  }

  /* Enhanced shadow on hover for toast */
  .shadow-2xl {
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }

  /* Smooth transition for all interactive elements */
  :deep(.v-btn) {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
</style>
